import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

export const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setPhotos(data)).catch(() => {});
    api.get("/site-config").then(({ data }) => setCfg(data)).catch(() => setCfg({}));
  }, []);

  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback((e) => { e?.stopPropagation(); setOpenIdx((i) => (i === null ? null : (i - 1 + photos.length) % photos.length)); }, [photos.length]);
  const next = useCallback((e) => { e?.stopPropagation(); setOpenIdx((i) => (i === null ? null : (i + 1) % photos.length)); }, [photos.length]);

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIdx, close, prev, next]);

  if (photos.length === 0) return null;

  return (
    <section id="gallery" className="relative py-24 md:py-32 px-5 md:px-10" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-[color:var(--alien)] uppercase tracking-[0.4em] text-xs mb-3">05 — Foto Live</p>
            <h2 className="font-display text-5xl md:text-7xl">
              {(cfg?.gallery_title || "DAL\nPALCO").split(/\r?\n/).map((line, i, arr) => (
                <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
              ))}
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm whitespace-pre-line">{cfg?.gallery_intro || "Momenti rubati ai nostri concerti. Scorri, tocca per ingrandire."}</p>
        </div>

        <div
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 md:mx-0 md:px-0 scroll-smooth"
          data-testid="gallery-scroll"
          style={{ scrollbarWidth: "thin" }}
        >
          {photos.map((p, i) => (
            <motion.button
              key={p.id}
              onClick={() => setOpenIdx(i)}
              data-testid={`gallery-thumb-${i}`}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="relative snap-start shrink-0 w-[75%] sm:w-[45%] md:w-[32%] lg:w-[24%] aspect-[4/5] group overflow-hidden border border-white/10 hover:border-[color:var(--alien)]"
            >
              <img src={p.image_url} alt={p.caption || p.event || "Foto live"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                {p.event && <p className="text-[color:var(--alien)] uppercase text-[10px] tracking-widest">{p.event}</p>}
                {p.caption && <p className="text-white text-sm mt-1 line-clamp-2">{p.caption}</p>}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {openIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            data-testid="gallery-lightbox"
          >
            <button onClick={close} data-testid="gallery-lightbox-close" className="absolute top-4 right-4 text-white/70 hover:text-[color:var(--alien)] p-2">
              <X size={28} />
            </button>
            {photos.length > 1 && (
              <button onClick={prev} data-testid="gallery-lightbox-prev" className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[color:var(--alien)] p-3 border border-white/20 bg-black/40">
                <ChevronLeft size={24} />
              </button>
            )}
            <motion.div
              key={openIdx}
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full flex flex-col items-center"
            >
              <img src={photos[openIdx].image_url} alt="" className="max-h-[80vh] max-w-full object-contain border border-white/10" />
              {(photos[openIdx].event || photos[openIdx].caption) && (
                <div className="mt-4 text-center">
                  {photos[openIdx].event && <p className="text-[color:var(--alien)] uppercase text-xs tracking-widest">{photos[openIdx].event}</p>}
                  {photos[openIdx].caption && <p className="text-white/80 mt-1">{photos[openIdx].caption}</p>}
                </div>
              )}
            </motion.div>
            {photos.length > 1 && (
              <button onClick={next} data-testid="gallery-lightbox-next" className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[color:var(--alien)] p-3 border border-white/20 bg-black/40">
                <ChevronRight size={24} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
